import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config/api";
import { useAuth } from "../../context/AuthContext";

// "Sign in with Apple" button — black pill per Apple HIG.
// Web path: loads Apple's JS SDK on-demand and uses signIn() popup.
// Native iOS path: uses @capacitor-community/apple-sign-in if available
// (falls back to web flow if the plugin isn't bundled yet).
//
// Both paths POST to /api/auth/apple/session-exchange and pipe the
// result through setSessionFromQR — same shape as login/Google.
const APPLE_JS_SRC = "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_SERVICES_ID = "tv.globaleyes.signin";

const AppleAuthButton = ({ label = "Sign in with Apple" }) => {
  const { setSessionFromQR } = useAuth();
  const [loading, setLoading] = useState(false);
  const [jsReady, setJsReady] = useState(false);
  const initedRef = useRef(false);

  // Lazy-load Apple's JS SDK once per session. We only do this on the
  // web — the native iOS plugin obviates this entirely.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (window.Capacitor?.isNativePlatform?.()) {
      setJsReady(true); // native: skip JS SDK; we use the plugin instead
      return undefined;
    }
    if (window.AppleID) { setJsReady(true); return undefined; }

    const existing = document.querySelector(`script[src="${APPLE_JS_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => setJsReady(true));
      if (existing.dataset.loaded === "1") setJsReady(true);
      return undefined;
    }

    const s = document.createElement("script");
    s.src = APPLE_JS_SRC;
    s.async = true;
    s.onload = () => { s.dataset.loaded = "1"; setJsReady(true); };
    s.onerror = () => toast.error("Apple sign-in couldn't load. Try again later.");
    document.body.appendChild(s);
    return undefined;
  }, []);

  // One-shot init of the Apple SDK with our Services ID + return URL.
  // Re-initing is harmless but spams the console, so we guard with a ref.
  const ensureInit = () => {
    if (initedRef.current) return;
    if (!window.AppleID) return;
    try {
      window.AppleID.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: "name email",
        redirectURI: `${window.location.origin}/auth/apple/callback`,
        state: Math.random().toString(36).slice(2),
        usePopup: true,
      });
      initedRef.current = true;
    } catch (e) {
      console.warn("AppleID.auth.init failed", e);
    }
  };

  const exchange = async (payload) => {
    const res = await axios.post(`${API}/auth/apple/session-exchange`, payload);
    setSessionFromQR(res.data);
    toast.success("Signed in with Apple.");
  };

  const handleClick = async () => {
    if (loading) return;
    setLoading(true);

    // Native iOS path — uses @capacitor-community/apple-sign-in which
    // presents the native Apple sheet (Face ID / Touch ID). The plugin
    // is only installed in the Capacitor iOS shell, never on web.
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      try {
        const mod = await import("@capacitor-community/apple-sign-in");
        const SignInWithApple = mod.SignInWithApple || mod.default?.SignInWithApple;
        if (!SignInWithApple) throw new Error("Apple plugin not bundled.");
        const opts = {
          clientId: APPLE_SERVICES_ID,
          redirectURI: `${window.location.origin}/auth/apple/callback`,
          scopes: "email name",
          state: Math.random().toString(36).slice(2),
          nonce: Math.random().toString(36).slice(2),
        };
        const result = await SignInWithApple.authorize(opts);
        const r = result?.response || result || {};
        const fullName = r.givenName || r.familyName
          ? [r.givenName, r.familyName].filter(Boolean).join(" ")
          : null;
        await exchange({
          identity_token: r.identityToken,
          authorization_code: r.authorizationCode || null,
          user_email: r.email || null,
          user_full_name: fullName,
          client_type: "ios",
        });
      } catch (err) {
        if (err?.message?.includes("cancel")) {
          // user backed out — no toast, no fuss
        } else {
          console.error("native apple sign-in failed", err);
          toast.error(err?.response?.data?.detail || "Apple sign-in failed. Please try again.");
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    // Web path — Apple JS SDK popup.
    ensureInit();
    if (!window.AppleID) {
      toast.error("Apple sign-in is still loading. Try again in a moment.");
      setLoading(false);
      return;
    }
    try {
      const result = await window.AppleID.auth.signIn();
      const { authorization, user } = result || {};
      const fullName = user?.name
        ? [user.name.firstName, user.name.lastName].filter(Boolean).join(" ")
        : null;
      await exchange({
        identity_token: authorization?.id_token,
        authorization_code: authorization?.code || null,
        user_email: user?.email || null,
        user_full_name: fullName,
        client_type: "web",
      });
    } catch (err) {
      // Apple JS SDK rejects with { error: 'popup_closed_by_user' } etc.
      const code = err?.error;
      if (code === "popup_closed_by_user" || code === "user_cancelled_authorize") {
        // silent — user just bailed
      } else {
        console.error("web apple sign-in failed", err);
        toast.error(err?.response?.data?.detail || "Apple sign-in failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!jsReady || loading}
      className="w-full flex items-center justify-center gap-2 bg-black hover:bg-gray-900 disabled:opacity-60 text-white font-medium py-2.5 rounded-md transition-colors border border-gray-700"
      data-testid="continue-with-apple-btn"
      aria-label={label}
    >
      <AppleLogo />
      <span className="text-sm">{loading ? "Connecting…" : label}</span>
    </button>
  );
};

const AppleLogo = () => (
  <svg width="16" height="16" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

export default AppleAuthButton;
